<template>
  <div v-if="show" class="dialog-overlay">
    <div class="dialog-content">
      <div class="dialog-header">
        <h2>Create New Course</h2>
        <button @click="$emit('close')" class="close-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <form @submit.prevent="submitCourse" class="create-form">
        <div class="form-row">
          <div class="form-group">
            <label for="title">Course Title (English)</label>
            <input 
              id="title"
              v-model="courseForm.title"
              type="text"
              required
              placeholder="Enter course title"
            >
          </div>
          <div class="form-group">
            <label for="title_ar">Course Title (Arabic)</label>
            <input 
              id="title_ar"
              v-model="courseForm.title_ar"
              type="text"
              placeholder="Enter Arabic course title"
            >
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="description">Description (English)</label>
            <QuillEditor 
              id="description"
              v-model="courseForm.description"
              placeholder="Enter course description"
              :minHeight="'150px'"
            />
          </div>
          <div class="form-group">
            <label for="description_ar">Description (Arabic)</label>
            <QuillEditor 
              id="description_ar"
              v-model="courseForm.description_ar"
              placeholder="Enter Arabic course description"
              dir="rtl"
              :minHeight="'150px'"
            />
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="instructor">Instructor</label>
            <select 
              id="instructor"
              v-model="courseForm.instructor_id"
              required
            >
              <option value="">Select Instructor</option>
              <option v-for="instructor in instructors" :key="instructor.id" :value="instructor.id">{{ instructor.first_name }} {{ instructor.last_name }}</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="category">Category</label>
            <select 
              id="category"
              v-model="courseForm.category_id"
              required
            >
              <option value="">Select Category</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.title }}</option>
            </select>
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="minutes">Duration (minutes)</label>
            <input 
              id="minutes"
              v-model="courseForm.minutes"
              type="number"
              min="0"
              placeholder="Enter duration in minutes"
            >
          </div>
          
          <div class="form-group">
            <label for="status">Status</label>
            <select 
              id="status"
              v-model="courseForm.status"
              required
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="price">Price</label>
            <input 
              id="price"
              v-model="courseForm.price"
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter price (optional)"
            >
          </div>
          
          <div class="form-group">
            <label for="science_behind_it">Science Behind It Video URL</label>
            <input 
              id="science_behind_it"
              v-model="courseForm.science_behind_it"
              type="url"
              placeholder="Enter video URL"
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="audio_url">Audio URL</label>
            <input 
              id="audio_url"
              v-model="courseForm.audio_url"
              type="url"
              placeholder="Enter audio URL"
            >
          </div>
          
          <div class="form-group">
            <label for="audio_url_ar">Audio URL (Arabic)</label>
            <input 
              id="audio_url_ar"
              v-model="courseForm.audio_url_ar"
              type="url"
              placeholder="Enter Arabic audio URL"
            >
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="audio_cover">Audio Cover URL</label>
            <input 
              id="audio_cover"
              v-model="courseForm.audio_cover"
              type="url"
              placeholder="Enter audio cover URL"
            >
          </div>
          
          <div class="form-group">
            <label for="audio_cover_ar">Audio Cover URL (Arabic)</label>
            <input 
              id="audio_cover_ar"
              v-model="courseForm.audio_cover_ar"
              type="url"
              placeholder="Enter Arabic audio cover URL"
            >
          </div>
        </div>

        <div class="form-row checkboxes">
          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox"
                v-model="courseForm.is_premium"
              >
              Premium Course
            </label>
          </div>
          
          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox"
                v-model="courseForm.top_course"
              >
              Top Course
            </label>
          </div>

          <div class="form-group checkbox">
            <label>
              <input 
                type="checkbox"
                v-model="courseForm.is_breathing"
              >
              Breathing Exercise
            </label>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" @click="$emit('close')" class="cancel-button">Cancel</button>
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            {{ isSubmitting ? 'Creating...' : 'Create Course' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'CreateCourseDialog',
  components: {
    QuillEditor
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    instructors: {
      type: Array,
      default: () => []
    },
    categories: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isSubmitting: false,
      courseForm: {
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        instructor_id: '',
        category_id: '',
        minutes: '',
        status: 'active',
        is_premium: false,
        price: null,
        science_behind_it: '',
        audio_cover: null,
        audio_cover_ar: null,
        audio_url: null,
        audio_url_ar: null,
        is_sleeping: 0,
        preview_status: 2,
        position_day: 0,
        position_night: 0,
        top_course: false,
        is_breathing: false
      }
    };
  },
  methods: {
    async submitCourse() {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        const formData = new FormData();
        
        Object.keys(this.courseForm).forEach(key => {
          if (this.courseForm[key] !== null && this.courseForm[key] !== '') {
            if (typeof this.courseForm[key] === 'boolean') {
              formData.append(key, this.courseForm[key] ? '1' : '0');
            } else {
              formData.append(key, this.courseForm[key]);
            }
          }
        });
        
        const response = await axios.post('https://api.kewaar.com/api/admin/courses2', formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        this.$emit('created', response.data.data);
        this.resetForm();
        
      } catch (error) {
        console.error('Error creating course:', error);
        this.$emit('error', error.response?.data?.message || 'Error creating course');
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.courseForm = {
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        instructor_id: '',
        category_id: '',
        minutes: '',
        status: 'active',
        is_premium: false,
        price: null,
        science_behind_it: '',
        audio_cover: null,
        audio_cover_ar: null,
        audio_url: null,
        audio_url_ar: null,
        is_sleeping: 0,
        preview_status: 2,
        position_day: 0,
        position_night: 0,
        top_course: false,
        is_breathing: false
      };
    }
  }
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  padding: 1rem;
}

.dialog-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #E2E8F0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-radius: 16px 16px 0 0;
}

.dialog-header h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: #64748B;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.close-button:hover {
  color: #1E293B;
  background: #F1F5F9;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.create-form {
  padding: 2rem;
}

.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.form-group {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 500;
  color: #1E293B;
  font-size: 0.875rem;
}

.form-group input[type="text"],
.form-group input[type="number"],
.form-group input[type="url"],
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  background-color: #F8FAFC;
  font-size: 0.875rem;
  color: #1E293B;
  transition: all 0.2s;
}

.form-group input[type="text"]:focus,
.form-group input[type="number"]:focus,
.form-group input[type="url"]:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #6366F1;
  background-color: white;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
}

.form-group select {
  appearance: none;
  padding-right: 2.5rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
}

.form-group.checkbox {
  display: flex;
  align-items: center;
}

.form-group.checkbox label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #1E293B;
}

.form-group.checkbox input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  margin: 0;
  border: 2px solid #E2E8F0;
  border-radius: 6px;
  transition: all 0.2s;
}

.form-group.checkbox input[type="checkbox"]:checked {
  background-color: #6366F1;
  border-color: #6366F1;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #E2E8F0;
}

.cancel-button {
  padding: 0.875rem 1.5rem;
  background: white;
  border: 2px solid #E2E8F0;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1E293B;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: #F8FAFC;
  border-color: #CBD5E0;
}

.submit-button {
  padding: 0.875rem 1.5rem;
  background: #6366F1;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(99, 102, 241, 0.1), 0 1px 2px rgba(99, 102, 241, 0.06);
}

.submit-button:hover:not(:disabled) {
  background: #4F46E5;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.2);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  .dialog-content {
    width: 95%;
    max-height: 95vh;
  }
}

@media (max-width: 640px) {
  .dialog-header,
  .create-form {
    padding: 1.25rem;
  }
  
  .dialog-header h2 {
    font-size: 1.25rem;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .cancel-button, .submit-button {
    width: 100%;
  }
}
</style> 